// CHARTS TICKED OFF IN A WORKED EXAMPLE, KEPT ON THIS DEVICE.
//
// The same trade sampleDrafts.js makes for the worksheets, for the same reason: "cannot be
// saved to a facility" is not "cannot be used". Somebody evaluating the product should be
// able to work down the eight sample charts and watch the "left this morning" count fall,
// because that IS the feature — a list you cannot mark is a report.
//
// Three things follow, and all three are said on screen rather than left to be discovered:
//
//   * nothing is saved to any facility and no API call is made;
//   * it does not follow the person to another browser or another machine;
//   * it is not PHI, because the residents are invented — which is the only reason browser
//     storage is acceptable here at all. This must never be pointed at live data.
//
// ── THE SAMPLE'S CLOCK IS NOT TODAY'S, SO NEITHER IS THE EXPIRY ──────────────
//
// On a live facility a mark expires because the window rolls past it. The worked example is
// built against a FIXED clock (the generator stamps one, so the drift test does not fail at
// midnight), so a wall-clock expiry would make the sample's marks vanish on the first reload
// and look broken. Sample marks are stored against the example's own `asOf` instead: change
// the example and the old marks stop matching, which is the behaviour you want from a demo.
//
// Every access is wrapped. localStorage THROWS outright in a private window with site data
// blocked — not returns null — so a reader that trusts it takes the whole screen down, in the
// one mode that exists so somebody can try the product without a facility.

const PREFIX = "survai.chartcheck.sample.v1";

const key = (asOf) => `${PREFIX}.${asOf || "UNKNOWN"}`;

/**
 * The resident ids ticked off in this sample, as a Set.
 *
 * Returns an EMPTY SET on a parse failure as well as on an absence, and never null: the
 * caller renders a list from it on every row, and a null here would be the one storage
 * failure that reaches the screen as a crash rather than as an unticked list.
 */
export function loadSampleReviewed(asOf) {
  try {
    const raw = window.localStorage.getItem(key(asOf));
    if (!raw) return new Set();
    const v = JSON.parse(raw);
    return Array.isArray(v) ? new Set(v.map(String)) : new Set();
  } catch {
    return new Set();
  }
}

/**
 * Tick or un-tick one chart. Returns the new Set, and whether it was actually stored.
 *
 * REPORTS THE FAILURE rather than swallowing it. A tick that silently does not persist is
 * worse here than no tick at all: the manager believes the chart is recorded as done, and
 * the only way she finds out is that it comes back tomorrow.
 */
export function toggleSampleReviewed(asOf, residentId, reviewed) {
  const next = loadSampleReviewed(asOf);
  if (reviewed) next.add(String(residentId));
  else next.delete(String(residentId));
  try {
    window.localStorage.setItem(key(asOf), JSON.stringify([...next]));
    return { set: next, stored: true };
  } catch {
    // The in-memory Set is still returned and still correct for this page view, so the click
    // does what it looks like it did until a reload. The caller says which it was.
    return { set: next, stored: false };
  }
}

// ── AND WHAT WAS DONE ABOUT IT ───────────────────────────────────────────────
//
// Kept in its own key rather than folded into the tick list, so an older build reading the
// tick list still finds an array of ids where it expects one. Same fixed clock, same wrapping,
// same honesty about a failed write.

const DKEY = (asOf) => `${PREFIX}.dispositions.${asOf || "UNKNOWN"}`;

/** residentId -> {disposition, note}, as a Map. Empty on any failure, never null. */
export function loadSampleDispositions(asOf) {
  try {
    const raw = window.localStorage.getItem(DKEY(asOf));
    if (!raw) return new Map();
    const v = JSON.parse(raw);
    if (!v || typeof v !== "object" || Array.isArray(v)) return new Map();
    return new Map(Object.entries(v).map(([k, x]) => [String(k), { disposition: x?.disposition ?? null, note: x?.note ?? null }]));
  } catch {
    return new Map();
  }
}

/** Record (or, with a null disposition, forget) one chart's answer. */
export function setSampleDisposition(asOf, residentId, disposition, note = null) {
  const next = loadSampleDispositions(asOf);
  if (disposition) next.set(String(residentId), { disposition, note: note || null });
  else next.delete(String(residentId));
  try {
    window.localStorage.setItem(DKEY(asOf), JSON.stringify(Object.fromEntries(next)));
    return { map: next, stored: true };
  } catch {
    return { map: next, stored: false };
  }
}

/** Forget every tick in this sample. Used by the screen's own "clear mine". */
export function clearSampleReviewed(asOf) {
  try {
    window.localStorage.removeItem(key(asOf));
    // BOTH KEYS. An answer left behind after the ticks were cleared would reappear against a
    // chart with no tick on it, which is a state the live screen cannot produce.
    window.localStorage.removeItem(DKEY(asOf));
    return true;
  } catch {
    return false;
  }
}
